/**
 * 把当前取景画成 512 正方形。静图按透明度选 PNG 或 JPEG；
 * 多帧 GIF 逐帧合成后再套同一矩阵。
 * 单帧颜色放得下时，这一帧用自己的原色表，不和别的帧挤在一起。
 * 某一帧超过 256 色时，全体才共用一张量化表，避免同一颜色忽明忽暗。
 */
import { applyPalette, GIFEncoder, quantize } from 'gifenc';
import { decompressFrames, parseGIF } from 'gifuct-js';

import {
  AVATAR_JPEG_QUALITY,
  avatarExportName,
  gifLoopCount,
} from './avatar-image-editor-file';
import {
  AVATAR_CROP_SIZE,
  avatarMatrix,
  type AvatarView,
} from './avatar-image-editor-math';

const DISPOSE_RESTORE = 3;
const DISPOSE_BACKGROUND = 2;
const DISPOSE_LEAVE = 1;
const PALETTE_SIZE = 256;

type GifPalette = {
  colors: number[][];
  transparentIndex: number;
  /** 原色到调色板下标。放得下全部颜色时使用，量化路径为 null。 */
  exact: Map<number, number> | null;
};

export async function exportAvatarImage(
  file: File,
  view: AvatarView,
  kind: 'static' | 'gif',
): Promise<File> {
  if (kind === 'gif') {
    return exportGif(file, view);
  }
  return exportStatic(file, view);
}

async function exportStatic(file: File, view: AvatarView): Promise<File> {
  const bitmap = await createImageBitmap(file, {
    imageOrientation: 'from-image',
  });
  try {
    const pixels = drawSource(
      bitmap,
      bitmap.width,
      bitmap.height,
      view,
      bitmap.width,
      bitmap.height,
    );
    const transparent = hasTransparentPixel(pixels);
    const canvas = document.createElement('canvas');
    canvas.width = AVATAR_CROP_SIZE;
    canvas.height = AVATAR_CROP_SIZE;
    const context = canvasContext(canvas);
    context.putImageData(
      imageData(pixels, AVATAR_CROP_SIZE, AVATAR_CROP_SIZE),
      0,
      0,
    );
    const blob = await canvasBlob(
      canvas,
      transparent ? 'image/png' : 'image/jpeg',
      transparent ? undefined : AVATAR_JPEG_QUALITY,
    );
    const ext = transparent ? 'png' : 'jpg';
    return new File([blob], avatarExportName(file.name, ext), {
      type: transparent ? 'image/png' : 'image/jpeg',
    });
  } finally {
    bitmap.close();
  }
}

async function exportGif(file: File, view: AvatarView): Promise<File> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const decoded = decodeGif(bytes);
  const rendered = renderGif(decoded, view);
  const plan = palettesForFrames(rendered);
  const encoder = GIFEncoder();
  rendered.forEach((pixels, index) => {
    const palette = plan.palettes[index];
    if (!palette) {
      throw new Error('gif palette');
    }
    writeGifFrame(
      encoder,
      pixels,
      palette,
      index === 0 ? decoded.repeat : 0,
      frameDelayMs(decoded.rawFrames[index]),
      !plan.shared || index === 0,
    );
  });
  encoder.finish();
  const encoded = encoder.bytes();
  return new File([copyBytes(encoded)], avatarExportName(file.name, 'gif'), {
    type: 'image/gif',
  });
}

function decodeGif(bytes: Uint8Array) {
  const parsed = parseGIF(bytes.buffer);
  const rawFrames = parsed.frames.filter(
    (frame) => 'image' in frame && frame.image,
  );
  const frames = decompressFrames(parsed, true);
  if (frames.length < 2 || frames.length !== rawFrames.length) {
    throw new Error('gif frame count');
  }
  const width = parsed.lsd.width;
  const height = parsed.lsd.height;
  if (width < 1 || height < 1) {
    throw new Error('gif screen');
  }
  const loop = gifLoopCount(bytes);
  return {
    width,
    height,
    repeat: loop === null ? -1 : loop,
    frames,
    rawFrames,
  };
}

function paintGif(
  decoded: ReturnType<typeof decodeGif>,
  view: AvatarView,
  onFrame: (pixels: Uint8ClampedArray, index: number) => void,
): void {
  const { width, height, frames } = decoded;
  const screen = new Uint8ClampedArray(width * height * 4);
  const backup = new Uint8ClampedArray(width * height * 4);
  const source = document.createElement('canvas');
  source.width = width;
  source.height = height;
  const sourceContext = canvasContext(source);
  const output = document.createElement('canvas');
  output.width = AVATAR_CROP_SIZE;
  output.height = AVATAR_CROP_SIZE;
  const outputContext = canvasContext(output);

  for (let index = 0; index < frames.length; index += 1) {
    const frame = frames[index];
    const patch = frame?.patch;
    if (!frame || !patch) {
      throw new Error('gif patch');
    }
    const disposal = frame.disposalType ?? DISPOSE_LEAVE;
    if (disposal === DISPOSE_RESTORE) {
      backup.set(screen);
    }
    blit(
      screen,
      width,
      height,
      patch,
      frame.dims.width,
      frame.dims.height,
      frame.dims.left,
      frame.dims.top,
    );
    sourceContext.putImageData(imageData(screen, width, height), 0, 0);
    onFrame(
      drawSource(
        source,
        width,
        height,
        view,
        width,
        height,
        output,
        outputContext,
        false,
      ),
      index,
    );
    if (disposal === DISPOSE_BACKGROUND) {
      clearRect(
        screen,
        width,
        height,
        frame.dims.left,
        frame.dims.top,
        frame.dims.width,
        frame.dims.height,
      );
    } else if (disposal === DISPOSE_RESTORE) {
      screen.set(backup);
    }
  }
}

function renderGif(
  decoded: ReturnType<typeof decodeGif>,
  view: AvatarView,
): Uint8ClampedArray[] {
  const rendered: Uint8ClampedArray[] = [];
  paintGif(decoded, view, (pixels) => {
    rendered.push(pixels);
  });
  return rendered;
}

function palettesForFrames(frames: Uint8ClampedArray[]): {
  palettes: GifPalette[];
  shared: boolean;
} {
  const individual: GifPalette[] = [];
  for (const pixels of frames) {
    const palette = paletteFromFrame(pixels);
    if (!palette) {
      const shared = quantizeFrames(frames);
      return { palettes: frames.map(() => shared), shared: true };
    }
    individual.push(palette);
  }
  return { palettes: individual, shared: false };
}

function paletteFromFrame(pixels: Uint8ClampedArray): GifPalette | null {
  const seen = new Map<number, number>();
  let transparent = false;
  for (let index = 0; index < pixels.length; index += 4) {
    const alpha = pixels[index + 3] ?? 0;
    if (alpha !== 255) {
      transparent = true;
    }
    if (alpha < 128) {
      continue;
    }
    const key = colorKey(
      pixels[index] ?? 0,
      pixels[index + 1] ?? 0,
      pixels[index + 2] ?? 0,
    );
    if (!seen.has(key)) {
      seen.set(key, seen.size);
      if (seen.size > PALETTE_SIZE) {
        return null;
      }
    }
  }
  const budget = transparent ? PALETTE_SIZE - 1 : PALETTE_SIZE;
  if (seen.size > budget) {
    return null;
  }
  const colors: number[][] = [];
  const exact = new Map<number, number>();
  const shift = transparent ? 1 : 0;
  for (const key of seen.keys()) {
    exact.set(key, colors.length + shift);
    colors.push([(key >> 16) & 255, (key >> 8) & 255, key & 255]);
  }
  return { colors, transparentIndex: transparent ? 0 : -1, exact };
}

function quantizeFrames(frames: Uint8ClampedArray[]): GifPalette {
  let transparent = false;
  let count = 0;
  for (const pixels of frames) {
    for (let index = 3; index < pixels.length; index += 4) {
      const alpha = pixels[index] ?? 0;
      if (alpha !== 255) {
        transparent = true;
      }
      if (alpha >= 128) {
        count += 1;
      }
    }
  }
  const budget = transparent ? PALETTE_SIZE - 1 : PALETTE_SIZE;
  if (count === 0) {
    if (!transparent) {
      throw new Error('gif palette');
    }
    return { colors: [], transparentIndex: 0, exact: null };
  }
  const tight = new Uint8ClampedArray(count * 4);
  let dest = 0;
  for (const pixels of frames) {
    for (let index = 0; index < pixels.length; index += 4) {
      if ((pixels[index + 3] ?? 0) < 128) {
        continue;
      }
      tight[dest] = pixels[index] ?? 0;
      tight[dest + 1] = pixels[index + 1] ?? 0;
      tight[dest + 2] = pixels[index + 2] ?? 0;
      tight[dest + 3] = 255;
      dest += 4;
    }
  }
  return {
    colors: quantize(tight, budget, { format: 'rgb565' }),
    transparentIndex: transparent ? 0 : -1,
    exact: null,
  };
}

function colorKey(red: number, green: number, blue: number): number {
  return ((red & 255) << 16) | ((green & 255) << 8) | (blue & 255);
}

function imageData(
  pixels: Uint8ClampedArray,
  width: number,
  height: number,
): ImageData {
  const image = new ImageData(width, height);
  image.data.set(pixels);
  return image;
}

function frameDelayMs(frame: unknown): number {
  if (typeof frame !== 'object' || frame === null || !('gce' in frame)) {
    return 0;
  }
  const control = frame.gce;
  if (
    typeof control !== 'object' ||
    control === null ||
    !('delay' in control) ||
    typeof control.delay !== 'number'
  ) {
    return 0;
  }
  return control.delay * 10;
}

function writeGifFrame(
  encoder: ReturnType<typeof GIFEncoder>,
  pixels: Uint8ClampedArray,
  palette: GifPalette,
  repeat: number,
  delay: number,
  includePalette: boolean,
): void {
  const { colors, transparentIndex } = palette;
  const index = indexFrame(pixels, palette);
  // 这一帧已经是整幅合成结果。播放器必须先清掉上一帧，
  // 否则透明像素会把上一帧露出来，叠成多层。
  // 透明色放在 0 号，和编码器写死的背景色下标对齐。
  encoder.writeFrame(index, AVATAR_CROP_SIZE, AVATAR_CROP_SIZE, {
    palette: includePalette ? encoderPalette(colors, transparentIndex) : null,
    delay,
    repeat,
    dispose: DISPOSE_BACKGROUND,
    transparent: transparentIndex >= 0,
    transparentIndex: transparentIndex >= 0 ? transparentIndex : 0,
  });
}

function indexFrame(
  pixels: Uint8ClampedArray,
  palette: GifPalette,
): Uint8Array {
  const { colors, transparentIndex, exact } = palette;
  if (exact) {
    const indexed = new Uint8Array(pixels.length / 4);
    for (
      let pixel = 0, index = 0;
      index < pixels.length;
      index += 4, pixel += 1
    ) {
      const alpha = pixels[index + 3] ?? 0;
      if (transparentIndex >= 0 && alpha < 128) {
        indexed[pixel] = transparentIndex;
        continue;
      }
      const key = colorKey(
        pixels[index] ?? 0,
        pixels[index + 1] ?? 0,
        pixels[index + 2] ?? 0,
      );
      indexed[pixel] =
        exact.get(key) ??
        nearestColor(
          pixels[index] ?? 0,
          pixels[index + 1] ?? 0,
          pixels[index + 2] ?? 0,
          colors,
          transparentIndex,
        );
    }
    return indexed;
  }
  if (colors.length === 0) {
    return new Uint8Array(pixels.length / 4);
  }
  const indexed = applyPalette(pixels, colors, 'rgb565');
  if (transparentIndex < 0) {
    return indexed;
  }
  for (
    let pixel = 0, alpha = 3;
    alpha < pixels.length;
    alpha += 4, pixel += 1
  ) {
    if ((pixels[alpha] ?? 255) < 128) {
      indexed[pixel] = transparentIndex;
    } else {
      indexed[pixel] = (indexed[pixel] ?? 0) + 1;
    }
  }
  return indexed;
}

function nearestColor(
  red: number,
  green: number,
  blue: number,
  colors: number[][],
  transparentIndex: number,
): number {
  let best = 0;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (let index = 0; index < colors.length; index += 1) {
    const color = colors[index];
    if (!color) {
      continue;
    }
    const distance =
      (color[0] - red) * (color[0] - red) +
      (color[1] - green) * (color[1] - green) +
      (color[2] - blue) * (color[2] - blue);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = index;
    }
  }
  return transparentIndex >= 0 ? best + 1 : best;
}

function encoderPalette(
  colors: number[][],
  transparentIndex: number,
): number[][] {
  if (transparentIndex < 0) {
    return colors;
  }
  return [[0, 0, 0, 0], ...colors];
}

function drawSource(
  source: CanvasImageSource,
  sourceWidth: number,
  sourceHeight: number,
  view: AvatarView,
  imageWidth: number,
  imageHeight: number,
  canvas = document.createElement('canvas'),
  context = canvasContext(canvas),
  smooth = true,
): Uint8ClampedArray {
  canvas.width = AVATAR_CROP_SIZE;
  canvas.height = AVATAR_CROP_SIZE;
  context.setTransform(1, 0, 0, 1, 0, 0);
  context.clearRect(0, 0, AVATAR_CROP_SIZE, AVATAR_CROP_SIZE);
  context.imageSmoothingEnabled = smooth;
  const matrix = avatarMatrix(view, imageWidth, imageHeight);
  context.setTransform(
    matrix.a,
    matrix.b,
    matrix.c,
    matrix.d,
    matrix.e,
    matrix.f,
  );
  context.drawImage(source, 0, 0, sourceWidth, sourceHeight);
  return new Uint8ClampedArray(
    context.getImageData(0, 0, AVATAR_CROP_SIZE, AVATAR_CROP_SIZE).data,
  );
}

function canvasContext(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const context = canvas.getContext('2d', { alpha: true });
  if (!context) {
    throw new Error('canvas');
  }
  return context;
}

function canvasBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number | undefined,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('encode'));
        }
      },
      type,
      quality,
    );
  });
}

function hasTransparentPixel(pixels: Uint8ClampedArray): boolean {
  for (let index = 3; index < pixels.length; index += 4) {
    if (pixels[index] !== 255) {
      return true;
    }
  }
  return false;
}

function blit(
  destination: Uint8ClampedArray,
  destinationWidth: number,
  destinationHeight: number,
  source: Uint8ClampedArray,
  sourceWidth: number,
  sourceHeight: number,
  left: number,
  top: number,
): void {
  const x0 = Math.max(0, left);
  const y0 = Math.max(0, top);
  const x1 = Math.min(destinationWidth, left + sourceWidth);
  const y1 = Math.min(destinationHeight, top + sourceHeight);
  for (let y = y0; y < y1; y += 1) {
    for (let x = x0; x < x1; x += 1) {
      const sourceIndex = ((y - top) * sourceWidth + (x - left)) * 4;
      const alpha = source[sourceIndex + 3] ?? 0;
      if (alpha === 0) {
        continue;
      }
      const destinationIndex = (y * destinationWidth + x) * 4;
      if (alpha === 255) {
        destination[destinationIndex] = source[sourceIndex] ?? 0;
        destination[destinationIndex + 1] = source[sourceIndex + 1] ?? 0;
        destination[destinationIndex + 2] = source[sourceIndex + 2] ?? 0;
        destination[destinationIndex + 3] = 255;
        continue;
      }
      const inverse = 255 - alpha;
      const destinationAlpha = destination[destinationIndex + 3] ?? 0;
      destination[destinationIndex] =
        ((source[sourceIndex] ?? 0) * alpha +
          (destination[destinationIndex] ?? 0) * inverse) /
        255;
      destination[destinationIndex + 1] =
        ((source[sourceIndex + 1] ?? 0) * alpha +
          (destination[destinationIndex + 1] ?? 0) * inverse) /
        255;
      destination[destinationIndex + 2] =
        ((source[sourceIndex + 2] ?? 0) * alpha +
          (destination[destinationIndex + 2] ?? 0) * inverse) /
        255;
      destination[destinationIndex + 3] =
        alpha + (destinationAlpha * inverse) / 255;
    }
  }
}

function clearRect(
  pixels: Uint8ClampedArray,
  width: number,
  height: number,
  left: number,
  top: number,
  rectWidth: number,
  rectHeight: number,
): void {
  const x0 = Math.max(0, left);
  const y0 = Math.max(0, top);
  const x1 = Math.min(width, left + rectWidth);
  const y1 = Math.min(height, top + rectHeight);
  for (let y = y0; y < y1; y += 1) {
    pixels.fill(0, (y * width + x0) * 4, (y * width + x1) * 4);
  }
}

function copyBytes(bytes: Uint8Array): Uint8Array<ArrayBuffer> {
  const buffer = new ArrayBuffer(bytes.byteLength);
  const copy = new Uint8Array(buffer);
  copy.set(bytes);
  return copy;
}
